import { setImpersonated } from '@app/actions/impersonated';
import { log, LOG_LEVELS, setHeadersAuthData } from '@common/config/app_logger';
import {
  fromMobileApp, isKA, isPS, toCamelCase
} from '@common/config/utils';
import { setApplicationError } from '@app/actions/aplication_error';
import { refreshReducersFromResponse, handle401UnauthorizedError } from './api_functions';
import { getDeviceId } from './device_id';
import { getCurrentLocalization } from './i18n';

const getWebClient = () => {
  if (isKA()) return 'KAWeb';
  if (isPS()) return 'PSWeb';
  return 'PGWeb';
};

const defaultHeaders = (deviceId: string) => {
  const deviceHeader = deviceId ? { WEBDEVICE_ID: deviceId } : null;
  return ({
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
      ...deviceHeader,
      'Language-Code': getCurrentLocalization(),
      WEB_CLIENT: getWebClient(),
      CF_IPCOUNTRY: 'CO'
    }
  });
};

export const setupFetchFunction = (store: { dispatch: (action: any) => any }) => {
  log(LOG_LEVELS.INFO, 'Setting up fetch function');

  window.mbwFetch = (input: RequestInfo | URL, init: RequestInit, { ignoreUserUpdate = false, ignorError404 = false } = {}) => new Promise(async (resolve, reject) => {
    const deviceId = fromMobileApp ? null : await getDeviceId();
    const { method } = init;
    window.fetch(input, {
      ...init,
      ...defaultHeaders(deviceId),
      credentials: 'include'
    }).then((response) => {
      if (response.headers) setHeadersAuthData(response.headers);
      if (response.ok) {
        if (response.status === 204) {
          resolve(toCamelCase({ success: 'ok' }));
          return;
        }
        store.dispatch(setImpersonated(response.headers.get('X-Impersonated')));
        response.json().then((responseJson) => {
          log(LOG_LEVELS.INFO, 'API', `URL ${ method } ${ input } request success`);
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('response', responseJson);
          }
          const res = toCamelCase(responseJson);
          refreshReducersFromResponse(res, { ignoreUserUpdate });
          resolve(res);
        }).catch((error) => {
          if (response.status === 204) resolve(toCamelCase({ success: 'ok' }));
          log(LOG_LEVELS.ERROR, 'API', `URL ${ method } ${ input } error parsing responce with error ${ error }`);
          reject(Object.assign(new Error(String(error)), { error: String(error) }));
        });
        return;
      }
      if (response.status === 401 || response.status === 404 || response.status >= 500) {
        log(LOG_LEVELS.INFO, 'API', `URL ${ method } ${ input } request responce status ${ response.status }`);
        if (handle401UnauthorizedError(response)) {
          reject(response);
          return;
        }
        if (ignorError404 && response.status === 404) {
          reject(Object.assign(new Error('Not Found'), { status: 404 }));
          return;
        }
        const { user } = window.store.getState();
        if (user && user.impersonated) {
          response.json().then((res) => {
            store.dispatch(setApplicationError({
              errorCode: response.status, errorText: res.error, errorUrl: input, statusText: null
            }));
          });
          return;
        }
        store.dispatch(setApplicationError({
          errorCode: response.status, errorUrl: input, statusText: response.statusText, errorText: null
        }));
      }

      log(LOG_LEVELS.ERROR, 'API', `URL ${ method } ${ input } response error ${ response.statusText }`);
      reject(response);
    }).catch((error) => {
      log(LOG_LEVELS.ERROR, 'API', `URL ${ method } ${ input } request error ${ error }`, error);
      reject(error);
    });
  });
};
